// @givmo/payments — Shift4 Components card-form wrapper.
//
// Ports the legacy `web/add-card/*.html` flow (jQuery + `shift4.createComponentGroup()
// .automount(...)` + `shift4.createToken(...)`) into a React component. The card fields
// render inside Shift4's OWN PCI iframes (the SAQ-A mechanism — Components keep their iframes
// by design); this wrapper never sees raw PAN/CVC. On submit it calls `createToken` and
// hands the opaque `token.id` to `onToken`.
//
// The publishable key + Shift4 instance come from `useShift4` (config, not code) — pass the
// instance in so the parent controls key sourcing from the F5 config endpoint.
import { useEffect, useId, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Button, cn, type ButtonProps } from '@givmo/design-system';
import type {
  Shift4ComponentGroup,
  Shift4ComponentGroupOptions,
  Shift4ComponentChangeEvent,
  Shift4ComponentType,
  Shift4Error,
  Shift4Instance,
  Shift4Subscription,
  Shift4Token,
} from '../lib/shift4-types';

/** The field components this form renders, in placeholder order. */
const FIELD_TYPES = ['number', 'expiry', 'cvc'] as const satisfies readonly Shift4ComponentType[];
type CardFieldType = (typeof FIELD_TYPES)[number];

const DEFAULT_FIELD_ERRORS: Record<CardFieldType, boolean> = {
  number: false,
  expiry: false,
  cvc: false,
};

const DEFAULT_COMPONENT_OPTIONS: Shift4ComponentGroupOptions = {
  style: {
    base: {
      color: '#000000',
      fontFamily:
        "'Lato', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
      fontSize: '16px',
      fontWeight: '400',
      '::placeholder': { color: '#9f9f9f' },
    },
    invalid: { color: '#d15296' },
  },
};

export interface CardFormProps {
  /** Instance from `useShift4(publishableKey)`. Render a loading state until non-null. */
  shift4: Shift4Instance;
  /** Called with the opaque token on a successful `createToken`. */
  onToken: (token: Shift4Token) => void;
  /** Called when `createToken` (or a field) reports an error. */
  onError?: (error: Shift4Error) => void;
  /** Called on every field-validity change (drives submit-button enablement upstream). */
  onChange?: (event: Shift4ComponentChangeEvent) => void;
  /**
   * Called with `true` when a submit starts `createToken` and with `false` once it settles (a token
   * or an error), so a caller can freeze the inputs its charge will carry for the whole round trip:
   * `onToken` is the one captured at the submit, so a later edit would show but not be charged.
   */
  onSubmittingChange?: (submitting: boolean) => void;
  /** Style/font options forwarded to `createComponentGroup`. */
  componentOptions?: Shift4ComponentGroupOptions;
  /** Whether the caller is still processing the token (for example, vaulting it via an API call). */
  busy?: boolean;
  /** Submit-button label (Flutter parity: donate-web uses "Next", iOS uses "Add card"). */
  submitLabel?: string;
  /** Appearance overrides for the design-system submit button. */
  submitButtonProps?: Partial<ButtonProps>;
  /**
   * Visible field labels. Default to English; a consumer that renders in another locale passes
   * translated values (the fields themselves live in Shift4's PCI iframes, so only these outer
   * `<label>`s are ours to localize). Same consumer-supplied pattern as `submitLabel`.
   */
  cardNumberLabel?: string;
  expiryLabel?: string;
  cvcLabel?: string;
  /** Optional class overrides. Defaults mirror the design-system `TextField`. */
  fieldClassName?: string;
  labelClassName?: string;
  fieldWrapperClassName?: string;
  /** Optional `data-testid` forwarded to the form root (e2e selector). */
  testId?: string;
  className?: string;
}

/**
 * A self-contained card-entry form backed by Shift4 Components. Creates one component per
 * `[data-shift4]` placeholder on a shared group, tracks per-field validity, and tokenizes
 * on submit.
 *
 * Why not `automount`: the LIVE SDK's component group is a plain record with NO event
 * surface (`{ createComponent, automount, updateComponentOptions }` — a group-level
 * `.on('change')` throws, taking the whole tree down with it). Events exist only on the
 * COMPONENTS, and their wire `change` payload carries `empty`/`error` per field, never a
 * `valid` flag. So the form mounts each field itself via `createComponent(...).mount(...)`
 * (equivalent to what `automount` does internally — the components register in the group,
 * so `createToken(group)` still collects them) and derives the submit-enablement aggregate
 * here: every field non-empty and error-free.
 */
export function CardForm({
  shift4,
  onToken,
  onError,
  onChange,
  onSubmittingChange,
  componentOptions,
  busy = false,
  submitLabel = 'Next',
  submitButtonProps,
  cardNumberLabel = 'Card number',
  expiryLabel = 'Expiration date',
  cvcLabel = 'Security code',
  fieldClassName,
  labelClassName,
  fieldWrapperClassName,
  testId,
  className,
}: CardFormProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const groupRef = useRef<Shift4ComponentGroup | null>(null);
  const [valid, setValid] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState(DEFAULT_FIELD_ERRORS);
  const baseId = useId();

  useEffect(() => {
    const formEl = formRef.current;
    if (!formEl) return;

    const group = shift4.createComponentGroup(componentOptions ?? DEFAULT_COMPONENT_OPTIONS);
    const fieldValid = new Map<Shift4ComponentType, boolean>(
      FIELD_TYPES.map((type) => [type, false]),
    );
    const subscriptions: Shift4Subscription[] = [];
    const components: Array<{ clear(): void }> = [];
    const hosts: HTMLElement[] = [];
    setValid(false);
    setFieldErrors(DEFAULT_FIELD_ERRORS);

    for (const type of FIELD_TYPES) {
      const host = formEl.querySelector(`[data-shift4="${type}"]`);
      if (!(host instanceof HTMLElement)) continue;
      const component = group.createComponent(type);
      component.mount(host);
      hosts.push(host);
      components.push(component);
      subscriptions.push(
        component.on('change', (event: Shift4ComponentChangeEvent) => {
          // A field is valid when it has content and the SDK reports no
          // error on it; the submit gate needs all three.
          fieldValid.set(type, event.empty === false && !event.error);
          setFieldErrors((current) => {
            const hasError = !!event.error;
            return current[type] === hasError ? current : { ...current, [type]: hasError };
          });
          const allValid = FIELD_TYPES.every((t) => fieldValid.get(t) === true);
          setValid(allValid);
          onChange?.({ ...event, valid: allValid });
        }),
      );
    }
    groupRef.current = group;

    return () => {
      // Best-effort teardown — drop the listeners and clear the fields so a
      // remount starts clean.
      for (const subscription of subscriptions) {
        try {
          subscription.unsubscribe();
        } catch {
          // Listener already gone with the SDK teardown; ignore.
        }
      }
      for (const component of components) {
        try {
          component.clear();
        } catch {
          // Some Shift4.js builds tear the iframe down on unmount; ignore.
        }
      }
      // Empty the hosts: the live SDK's mount() APPENDS to a non-empty host,
      // so without this a StrictMode dev double-mount stacks two live iframes
      // per field (the visible pair diverges from the group tokenize reads).
      // Makes the effect idempotent under remount-on-same-DOM; no-op in prod.
      for (const host of hosts) {
        host.replaceChildren();
      }
      groupRef.current = null;
    };
    // Keyed on `shift4` only: re-mounting on every `componentOptions`/handler change would
    // thrash the PCI iframes. Callers keep options stable (the legacy pages were static).
    // The shared ESLint config does not enable the react-hooks plugin, so there is no
    // exhaustive-deps rule to disable here.
  }, [shift4]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const group = groupRef.current;
    if (!group || submitting || busy) return;
    setSubmitting(true);
    onSubmittingChange?.(true);
    try {
      const token = await shift4.createToken(group);
      onToken(token);
    } catch (err: unknown) {
      const error: Shift4Error =
        err && typeof err === 'object' && 'message' in err
          ? (err as Shift4Error)
          : { message: String(err) };
      onError?.(error);
    } finally {
      setSubmitting(false);
      onSubmittingChange?.(false);
    }
  }

  const submitBusy = submitting || busy;
  const submitTestId = testId ? `${testId}-submit` : submitButtonProps?.testId;

  const fieldGroup = (type: CardFieldType, label: string) => {
    const id = `${baseId}-${type}`;
    const error = fieldErrors[type];
    return (
      <div className={cn('flex flex-col items-stretch', fieldWrapperClassName)}>
        <label
          htmlFor={id}
          className={cn(
            'mb-8 font-sans text-s12 font-w600 tracking-givmo text-black',
            labelClassName,
          )}
        >
          {label}
        </label>
        <div
          id={id}
          data-shift4={type}
          className={cn(
            'min-h-48 w-full rounded-all12 border bg-white p-12',
            'font-sans text-s16 font-w400 leading-h15 tracking-givmo text-black',
            'placeholder:text-gray60 outline-none transition-shadow',
            // The TextField's idle boundary (EP-WEB45): neutral-500, 4.68:1 on white. Flutter's
            // gray60 was 2.65:1, under SC 1.4.11's 3:1, and left the card box the faintest field.
            'border-neutral-500',
            'focus-within:border-blue focus-within:shadow-focus-glow',
            error && 'border-magenta focus-within:border-magenta',
            fieldClassName,
          )}
        />
      </div>
    );
  };

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit}
      className={className}
      {...(testId === undefined ? {} : { 'data-testid': testId })}
    >
      {fieldGroup('number', cardNumberLabel)}
      {fieldGroup('expiry', expiryLabel)}
      {fieldGroup('cvc', cvcLabel)}
      <Button
        appearance="saas"
        tone="primary"
        size="md"
        fullWidth
        {...submitButtonProps}
        type="submit"
        disabled={!valid || submitBusy || submitButtonProps?.disabled}
        loading={submitBusy || submitButtonProps?.loading}
        testId={submitTestId}
      >
        {submitLabel}
      </Button>
    </form>
  );
}
